import React from 'react';
import { Users, Shield, UserPlus, RefreshCw, Phone, Trash2, Upload, Smile, Link2, Search } from 'lucide-react';
import { Button, Input, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, Tabs, TabsList, TabsTrigger, TabsContent } from '@hts/ui';
import { useSearchParams } from 'react-router-dom';
import { useGet, apiPatch, apiPost, apiDelete } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { UserDrawer } from '../../components/UserDrawer.js';
import { ROLE_COLORS, ROLE_LABEL, Avatar } from '../../lib/roles.js';

type Role = 'owner' | 'admin' | 'accounting' | 'dispatcher' | 'tech' | 'viewer';

interface MembershipView {
  id: string;
  userId: string;
  email: string;
  name: string; // computed display name (firstName + lastName fallback legacy/email)
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  phone: string | null;
  photoUrl: string | null;
  deactivated: boolean;
  role: Role;
}

interface UserDetail extends MembershipView {
  notes: string | null;
  entra: { entraOid: string | null; entraUpn: string | null; email: string };
  memberships: Array<{ tenantId: string; role: Role }>;
  threecxAvailableExtensions: string[];
  technician: {
    id: string;
    name: string;
    cwMemberId: number | null;
    threecxExtension: string | null;
    tier: string | null;
    active: boolean;
    userId: string | null;
  } | null;
  skills: Array<{
    techSkillId: string | null;
    skillId: string;
    skillName: string;
    category: string;
    level: number;
    source: 'inferred' | 'manager' | 'admin' | 'self';
  }>;
}

interface ThreeCxDirectoryItem {
  id: number; extension: string; displayName: string; email: string | null;
  enabled: boolean; assignedUserId: string | null;
}
interface ThreeCxDirectoryResponse { status: 'ready' | 'unavailable'; items: ThreeCxDirectoryItem[] }

export function availableThreeCxItems(items: ThreeCxDirectoryItem[], current: string, userId: string | null): ThreeCxDirectoryItem[] {
  return items.filter((item) => item.extension === current || !item.assignedUserId || item.assignedUserId === userId);
}

// Curated common country calling codes (E.164 dial-in). Not exhaustive —
// "Other" covers the rest with manual entry.
const COUNTRY_CODES: Array<{ code: string; label: string }> = [
  { code: '+1', label: '+1 US/CA' },
  { code: '+44', label: '+44 UK' },
  { code: '+61', label: '+61 AU' },
  { code: '+31', label: '+31 NL' },
  { code: '+49', label: '+49 DE' },
  { code: '+33', label: '+33 FR' },
  { code: '+34', label: '+34 ES' },
  { code: '+39', label: '+39 IT' },
  { code: '+353', label: '+353 IE' },
  { code: '+52', label: '+52 MX' },
  { code: '+55', label: '+55 BR' },
  { code: '+91', label: '+91 IN' },
  { code: '+65', label: '+65 SG' },
  { code: '', label: 'Other…' },
];

// Built-in stock avatars: deterministic SVG data URLs (no binary assets needed).
const STOCK_AVATAR_COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#059669', '#0891b2', '#4f46e5', '#b45309'];
function stockAvatar(seed: string, index: number): string {
  const color = STOCK_AVATAR_COLORS[index % STOCK_AVATAR_COLORS.length];
  const letter = (seed || '?')[0]?.toUpperCase() ?? '?';
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256"><rect width="256" height="256" rx="128" fill="' + color + '"/><text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="system-ui,sans-serif" font-size="120" font-weight="700" fill="#fff">' + letter + '</text></svg>';
  return 'data:image/svg+xml;base64,' + btoa(svg);
}

// Resize an uploaded image to ~256x256 and return a JPEG data URL (≤256KB).
// Client-side resize keeps the stored payload small because we inline avatars
// into photo_url as data URLs (no blob storage yet — see API service comment).
async function fileToResizedDataUrl(file: File, maxDim = 256): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  let quality = 0.85;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);
  while (dataUrl.length > 256 * 1024 && quality > 0.3) {
    quality -= 0.15;
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }
  if (dataUrl.length > 256 * 1024) throw new Error('Photo too large even after resize (max 256KB)');
  return dataUrl;
}

const ROLES: Role[] = ['owner', 'admin', 'dispatcher', 'tech', 'viewer'];

const LEVEL_LABELS: Record<number, string> = {
  0: 'None', 1: 'Beginner', 2: 'Intermediate', 3: 'Advanced', 4: 'Expert',
};

const LEVEL_COLORS: Record<number, string> = {
  0: 'bg-gray-100 text-gray-400 dark:bg-gray-800',
  1: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
  2: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300',
  3: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300',
  4: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const labeledInput = 'block text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide mb-1';
const inputCls = 'w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none';

export function UsersPage() {
  const { data, loading, error, refetch } = useGet<{ items: MembershipView[]; total: number }>('/api/v1/users');
  const [saving, setSaving] = React.useState<string | null>(null);
  const [showDeactivated, setShowDeactivated] = React.useState(false);
  const [err, setErr] = React.useState<string | undefined>();

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteFirstName, setInviteFirstName] = React.useState('');
  const [inviteLastName, setInviteLastName] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState<Role>('viewer');
  const [inviteBusy, setInviteBusy] = React.useState(false);
  const [inviteErr, setInviteErr] = React.useState<string | undefined>();

  // Remove confirm dialog state
  const [removeTarget, setRemoveTarget] = React.useState<MembershipView | null>(null);
  const [removeDeactivate, setRemoveDeactivate] = React.useState(false);
  const [removeBusy, setRemoveBusy] = React.useState(false);
  const [removeErr, setRemoveErr] = React.useState<string | undefined>();

  // Detail drawer state
  // Deep-link support: /admin/users?userId=<id> opens that user's drawer
  // (used by SchedulePage click-through on a technician row).
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedUserId = searchParams.get('userId');
  const [detailUserId, setDetailUserId] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (linkedUserId) {
      setDetailUserId(linkedUserId);
      setSearchParams({}, { replace: true });
    }
  }, [linkedUserId, setSearchParams]);

  const users = data?.items ?? [];
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<'all' | Role>('all');
  const filtered = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((u) => {
      if (!showDeactivated && u.deactivated) return false;
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (!needle) return true;
      return (u.name + ' ' + u.email + ' ' + (u.title ?? '')).toLowerCase().includes(needle);
    });
  }, [users, search, roleFilter]);

  async function changeRole(userId: string, role: string) {
    setSaving(userId);
    setErr(undefined);
    try {
      await apiPatch(`/api/v1/users/${userId}`, { role });
      refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to change role');
    } finally {
      setSaving(null);
    }
  }

  async function invite() {
    setInviteBusy(true);
    setInviteErr(undefined);
    try {
      await apiPost('/api/v1/users', {
        email: inviteEmail.trim(),
        firstName: inviteFirstName.trim() || undefined,
        lastName: inviteLastName.trim() || undefined,
        role: inviteRole,
      });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteRole('viewer');
      refetch();
    } catch (e) {
      setInviteErr(e instanceof Error ? e.message : 'Failed to invite user');
    } finally {
      setInviteBusy(false);
    }
  }

  async function removeUser() {
    if (!removeTarget) return;
    setRemoveBusy(true);
    setRemoveErr(undefined);
    try {
      await apiDelete(`/api/v1/users/${removeTarget.userId}`, { deactivate: removeDeactivate });
      setRemoveTarget(null);
      setRemoveDeactivate(false);
      refetch();
    } catch (e) {
      setRemoveErr(e instanceof Error ? e.message : 'Failed to remove user');
    } finally {
      setRemoveBusy(false);
    }
  }

  const roleCounts = ROLES.map((r) => ({ role: r, count: users.filter((u) => u.role === r).length }));

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">Users &amp; Roles</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
            {users.length} user{users.length === 1 ? '' : 's'} in this tenant — add people by email or manage them after their first Microsoft sign-in
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={refetch}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button variant="primary" size="sm" onClick={() => { setInviteOpen(true); setInviteErr(undefined); }}>
            <UserPlus className="h-4 w-4" />
            Invite user
          </Button>
        </div>
      </div>

      {err && (
        <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">{err}</div>
      )}

      {/* Roles summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {roleCounts.map((r) => (
          <div key={r.role} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-3">
            <p className="text-2xl font-bold text-[hsl(var(--content))] tabular-nums">{r.count}</p>
            <p className="text-sm font-medium text-[hsl(var(--content))]">{ROLE_LABEL[r.role]}</p>
          </div>
        ))}
      </div>

      {/* Users table */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <Users className="h-4 w-4 text-[hsl(var(--content-subtle))]" />
            <h3 className="text-sm font-semibold text-[hsl(var(--content))]">All Users</h3>
          </div>
          <div className="relative flex-1 max-w-sm sm:ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--content-placeholder))]" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, title…"
              aria-label="Search users"
              className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] pl-8 pr-3 py-1.5 text-sm text-[hsl(var(--content))] placeholder:text-[hsl(var(--content-placeholder))] focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none"
            />
          <label className="flex items-center gap-1.5 text-xs text-[hsl(var(--content-subtle))] cursor-pointer ml-2"><input type="checkbox" checked={showDeactivated} onChange={(e) => setShowDeactivated(e.target.checked)} className="rounded border-[hsl(var(--border))]" />Show deactivated</label>
          </div>
          <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Filter by role">
            {(['all', ...ROLES] as const).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRoleFilter(r)}
                aria-pressed={roleFilter === r}
                className={'rounded-full px-2.5 py-1 text-xs font-medium transition-colors ' + (roleFilter === r
                  ? 'bg-brand-600 text-white'
                  : 'text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]')}
              >
                {r === 'all' ? 'All' : ROLE_LABEL[r]}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="p-5"><CardSkeleton /></div>
        ) : error ? (
          <div className="px-5 py-8 text-center text-sm text-[hsl(var(--content-subtle))]">
            Failed to load users ({error}). You may not have admin access.
          </div>
        ) : users.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-[hsl(var(--content-subtle))]">
            No users yet. Invite someone by email, or users are created automatically when they sign in with their Microsoft account.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]">
                  {['User', 'Email', 'Status', 'Role', ''].map((h) => (
                    <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} className="py-10 px-4 text-center text-sm text-[hsl(var(--content-subtle))]">No users match your search.</td></tr>
                ) : (
                filtered.map((user) => (
                  <tr key={user.id} className="hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                    <td className="py-3 px-4">
                      <button
                        onClick={() => setDetailUserId(user.userId)}
                        className="flex items-center gap-2.5 text-left group"
                        aria-label={`Open details for ${user.email}`}
                      >
                        <Avatar name={user.name} email={user.email} photoUrl={user.photoUrl} size="sm" />
                        <span>
                          <span className="font-medium text-[hsl(var(--content))] group-hover:underline">{user.name || user.email.split('@')[0]}</span>
                          {user.title && <span className="block text-xs text-[hsl(var(--content-subtle))]">{user.title}</span>}
                        </span>
                      </button>
                    </td>
                    <td className="py-3 px-4 text-[hsl(var(--content-subtle))]">{user.email}</td>
                    <td className="py-3 px-4">
                      {user.deactivated ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">Deactivated</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />Active
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[user.role] ?? ''}`}>
                        {ROLE_LABEL[user.role] ?? user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <select
                          value={user.role}
                          disabled={saving === user.userId}
                          onChange={(e) => changeRole(user.userId, e.target.value)}
                          className="text-xs rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1 text-[hsl(var(--content))] disabled:opacity-50"
                          aria-label={`Change role for ${user.email}`}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                          ))}
                        </select>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => { setRemoveTarget(user); setRemoveDeactivate(false); setRemoveErr(undefined); }}
                          aria-label={`Remove ${user.email} from tenant`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite user</DialogTitle>
            <DialogDescription>
              Pre-provision a user by email. They get access when they sign in with their Microsoft account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label htmlFor="invite-email" className={labeledInput}>Email</label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="name@company.com"
                autoComplete="off"
              />
            </div>
            <div>
              <label htmlFor="invite-first" className={labeledInput}>First name (optional)</label>
              <Input
                id="invite-first"
                autoComplete="given-name"
                value={inviteFirstName}
                onChange={(e) => setInviteFirstName(e.target.value)}
                placeholder="Jane"
              />
            </div>
            <div>
              <label htmlFor="invite-last" className={labeledInput}>Last name (optional)</label>
              <Input
                id="invite-last"
                autoComplete="family-name"
                value={inviteLastName}
                onChange={(e) => setInviteLastName(e.target.value)}
                placeholder="Doe"
              />
            </div>
            <div>
              <label htmlFor="invite-role" className={labeledInput}>Role</label>
              <select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Role)}
                className={inputCls}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                ))}
              </select>
            </div>
            {inviteErr && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{inviteErr}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={inviteBusy || !inviteEmail.includes('@')} onClick={invite}>
              {inviteBusy ? 'Inviting…' : 'Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirm dialog */}
      <Dialog open={removeTarget !== null} onOpenChange={(open) => { if (!open) setRemoveTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove user</DialogTitle>
            <DialogDescription>
              Remove {removeTarget?.email} from this tenant? They will immediately lose access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-start gap-2.5 text-sm text-[hsl(var(--content))]">
              <input
                type="checkbox"
                checked={removeDeactivate}
                onChange={(e) => setRemoveDeactivate(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-[hsl(var(--border))]"
              />
              <span>
                Also deactivate the user account (only applies when they belong to no other tenants; blocks sign-in until re-invited)
              </span>
            </label>
            {removeErr && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{removeErr}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button variant="danger" size="sm" disabled={removeBusy} onClick={removeUser}>
              {removeBusy ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User detail drawer */}
      <UserDrawer
        userId={detailUserId}
        onClose={() => { setDetailUserId(null); refetch(); }}
        canEdit={true}
        isSelf={false}
      />

      {/* Role permissions reference */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-4 w-4 text-[hsl(var(--content-subtle))]" />
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Role Permissions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="py-2 px-3 text-left font-semibold text-[hsl(var(--content-subtle))]">Permission</th>
                {['Owner', 'Admin', 'Dispatcher', 'Technician', 'Viewer'].map((r) => (
                  <th key={r} className="py-2 px-3 text-center font-semibold text-[hsl(var(--content-subtle))]">{r}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--border))]">
              {[
                ['View tickets', true, true, true, true, true],
                ['Assign tickets', true, true, true, false, false],
                ['Manage integrations', true, true, false, false, false],
                ['Configure boards/policies', true, true, false, false, false],
                ['View audit log', true, true, false, false, false],
                ['Manage users', true, true, false, false, false],
              ].map(([perm, ...vals]) => (
                <tr key={String(perm)} className="hover:bg-[hsl(var(--surface-subtle))]">
                  <td className="py-2 px-3 text-[hsl(var(--content))]">{perm}</td>
                  {(vals as boolean[]).map((v, i) => (
                    <td key={i} className="py-2 px-3 text-center">
                      {v ? <span className="text-green-600">✓</span> : <span className="text-[hsl(var(--content-placeholder))]">–</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ThreeCxExtensionSelector({ id, value, items, disabled, loading, onChange }: { id: string; value: string; items: ThreeCxDirectoryItem[]; disabled: boolean; loading: boolean; onChange: (value: string) => void }) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const selected = items.find((item) => item.extension === value);
  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => !needle || (item.extension + ' ' + item.displayName + ' ' + (item.email ?? '')).toLowerCase().includes(needle)).slice(0, 100);
  }, [items, query]);
  return <div className="relative">
    {selected && <div className="mb-2 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm dark:border-blue-900 dark:bg-blue-950/30"><span><span className="font-mono font-semibold">{selected.extension}</span> — {selected.displayName}</span><button type="button" className="text-xs font-semibold text-red-600 hover:underline" onClick={() => onChange('')} disabled={disabled}>Clear</button></div>}
    <Input id={id} role="combobox" aria-expanded={open} aria-controls={id + '-options'} aria-autocomplete="list" value={query} disabled={disabled} placeholder={loading ? 'Loading live 3CX directory…' : 'Search extension or name'} onFocus={() => setOpen(true)} onChange={(event) => { setQuery(event.target.value); setOpen(true); }} onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }} />
    {open && !disabled && <div id={id + '-options'} role="listbox" className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-xl">
      {filtered.length ? filtered.map((item) => <button type="button" role="option" aria-selected={item.extension === value} key={item.id} disabled={!item.enabled} onMouseDown={(event) => event.preventDefault()} onClick={() => { onChange(item.extension); setQuery(''); setOpen(false); }} className="block w-full px-3 py-2 text-left text-sm hover:bg-[hsl(var(--surface-hover))] focus:bg-[hsl(var(--surface-hover))] disabled:opacity-50"><span className="font-mono font-semibold">{item.extension}</span> — {item.displayName}{!item.enabled ? ' (disabled)' : ''}</button>) : <p className="px-3 py-3 text-sm text-[hsl(var(--content-subtle))]">No matching available extensions.</p>}
    </div>}
  </div>;
}

function UserDetailDrawer({ userId, onClose }: { userId: string | null; onClose: () => void }) {
  const { data, loading, error, refetch } = useGet<UserDetail | undefined>(userId ? `/api/v1/users/${userId}` : null, [userId]);
  const directory = useGet<ThreeCxDirectoryResponse | undefined>(userId ? '/api/v1/users/3cx-directory' : null, [userId]);
  const [saveErr, setSaveErr] = React.useState<string | undefined>();
  const [busy, setBusy] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState<number | null>(null);

  const [form, setForm] = React.useState({
    firstName: '',
    lastName: '',
    title: '',
    phone: '',
    photoUrl: '',
    notes: '',
    entraUpn: '',
    threecxExtension: '',
  });
  // Phone is edited as country code + subscriber number; combined on save.
  const [phoneCc, setPhoneCc] = React.useState('+1');
  const [phoneRest, setPhoneRest] = React.useState('');
  const [photoMode, setPhotoMode] = React.useState<'none' | 'upload' | 'stock' | 'custom'>('custom');
  const [photoErr, setPhotoErr] = React.useState<string | undefined>();
  const loadedFor = React.useRef<string | null>(null);

  // Add-skill form (Skills tab)
  const [newSkillName, setNewSkillName] = React.useState('');
  const [newSkillLevel, setNewSkillLevel] = React.useState(2);

  React.useEffect(() => {
    if (data && loadedFor.current !== data.userId) {
      loadedFor.current = data.userId;
      setForm({
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        title: data.title ?? '',
        phone: data.phone ?? '',
        photoUrl: data.photoUrl ?? '',
        notes: data.notes ?? '',
        entraUpn: data.entra?.entraUpn ?? '',
        threecxExtension: data.technician?.threecxExtension ?? '',
      });
      // Pre-split an existing E.164 number into cc + rest for the selects.
      const m = (data.phone ?? '').match(/^(\+\d{1,3})(\d{4,})$/);
      if (m) { setPhoneCc(m[1] ?? '+1'); setPhoneRest(m[2] ?? ''); }
      else { setPhoneCc('+1'); setPhoneRest(''); }
      setPhotoMode(data.photoUrl ? (data.photoUrl.startsWith('data:') ? 'upload' : 'custom') : 'custom');
    }
  }, [data]);

  async function saveDetails() {
    if (!userId) return;
    setBusy(true);
    setSaveErr(undefined);
    try {
      const currentExtension = data?.technician?.threecxExtension ?? '';
      if (form.threecxExtension !== currentExtension) {
        if (directory.loading || directory.error || directory.data?.status !== 'ready') throw new Error('Wait for the live 3CX directory before changing the extension');
        if (form.threecxExtension) {
          const selected = directory.data.items.find((item) => item.extension === form.threecxExtension);
          if (!selected || !selected.enabled || (selected.assignedUserId && selected.assignedUserId !== userId)) throw new Error('Select an available extension from the live 3CX directory');
        }
      }
      const rest = phoneRest.replace(/\D/g, '');
      const phone = (phoneCc && rest) ? phoneCc + rest : null;
      if (phone && !/^\+[1-9]\d{7,14}$/.test(phone)) {
        throw new Error('Phone must be a valid E.164 number, e.g. +1 5551234567');
      }
      await apiPatch(`/api/v1/users/${userId}/details`, {
        firstName: form.firstName || null,
        lastName: form.lastName || null,
        title: form.title || null,
        phone,
        photoUrl: form.photoUrl || null,
        notes: form.notes || null,
        entraUpn: form.entraUpn || null,
        threecxExtension: form.threecxExtension || null,
      });
      setSavedAt(Date.now());
      refetch();
      setTimeout(() => setSavedAt(null), 2000);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  async function onPhotoFile(file: File | undefined) {
    setPhotoErr(undefined);
    if (!file) return;
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      setForm((f) => ({ ...f, photoUrl: dataUrl }));
      setPhotoMode('upload');
    } catch (e) {
      setPhotoErr(e instanceof Error ? e.message : 'Failed to process photo');
    }
  }

  async function setSkill(skillId: string, level: number) {
    if (!userId) return;
    setSaveErr(undefined);
    try {
      await apiPatch(`/api/v1/users/${userId}/skills/${skillId}`, { level });
      refetch();
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Failed to set skill level');
    }
  }

  async function addSkill() {
    if (!userId || !newSkillName.trim()) return;
    setSaveErr(undefined);
    setBusy(true);
    try {
      await apiPost(`/api/v1/users/${userId}/skills`, {
        name: newSkillName.trim(),
        level: newSkillLevel,
      });
      setNewSkillName('');
      setNewSkillLevel(2);
      refetch();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Failed to add skill');
    } finally {
      setBusy(false);
    }
  }

  const skillsByCategory = React.useMemo(() => {
    const map = new Map<string, UserDetail['skills']>();
    for (const s of data?.skills ?? []) {
      const cat = s.category || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(s);
    }
    return map;
  }, [data]);

  const extOptions = React.useMemo(() => availableThreeCxItems(directory.data?.items ?? [], form.threecxExtension, userId), [directory.data?.items, form.threecxExtension, userId]);

  return (
    <Drawer open={userId !== null} onOpenChange={(open) => { if (!open) { loadedFor.current = null; onClose(); } }}>
      <DrawerContent className="max-w-xl w-full">
        <DrawerHeader>
          <DrawerTitle>User details</DrawerTitle>
          <DrawerDescription>Profile, account &amp; roles, skills, 3CX &amp; Microsoft</DrawerDescription>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">
          {loading ? (
            <CardSkeleton />
          ) : error || !data ? (
            <p className="text-sm text-[hsl(var(--content-subtle))]">Failed to load user details {error ? `(${error})` : ''}.</p>
          ) : (
            <>
              {/* Header card */}
              <div className="flex items-center gap-4">
                <Avatar name={data.name} email={data.email} photoUrl={form.photoUrl || data.photoUrl} size="lg" />
                <div className="min-w-0">
                  <p className="font-semibold text-[hsl(var(--content))] truncate">{data.name || data.email.split('@')[0]}</p>
                  <p className="text-sm text-[hsl(var(--content-subtle))] truncate">{data.email}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[data.role] ?? ''}`}>
                      {ROLE_LABEL[data.role] ?? data.role}
                    </span>
                    {data.deactivated && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={async () => {
                          if (!userId) return;
                          setBusy(true); setSaveErr(undefined);
                          try {
                            await apiPost(`/api/v1/users/${userId}/reactivate`, {});
                            refetch();
                          } catch (e) {
                            setSaveErr(e instanceof Error ? e.message : "Failed to reactivate");
                          } finally {
                            setBusy(false);
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
                      >
                        <RefreshCw className="h-3 w-3" /> Reactivate
                      </button>
                    )}
                    {data.technician && (
                      <span className="text-xs text-[hsl(var(--content-subtle))]">
                        CW member #{data.technician.cwMemberId ?? '—'}{data.technician.tier ? ` · ${data.technician.tier}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {saveErr && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{saveErr}</p>}

              <Tabs defaultValue="profile">
                <TabsList aria-label="User detail sections">
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  <TabsTrigger value="account">Account &amp; Roles</TabsTrigger>
                  <TabsTrigger value="skills">Skills</TabsTrigger>
                  <TabsTrigger value="integrations">3CX &amp; Microsoft</TabsTrigger>
                </TabsList>

                {/* ── Profile tab ─────────────────────────────────────────── */}
                <TabsContent value="profile" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="ud-first" className={labeledInput}>First name</label>
                      <Input id="ud-first" autoComplete="given-name" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
                    </div>
                    <div>
                      <label htmlFor="ud-last" className={labeledInput}>Last name</label>
                      <Input id="ud-last" autoComplete="family-name" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
                    </div>
                    <div>
                      <label htmlFor="ud-title" className={labeledInput}>Title</label>
                      <Input id="ud-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Senior Technician" />
                    </div>
                    <div>
                      <span className={labeledInput}>Phone</span>
                      <div className="flex gap-2">
                        <label htmlFor="ud-phone-cc" className="sr-only">Country code</label>
                        <select
                          id="ud-phone-cc"
                          value={phoneCc}
                          onChange={(e) => setPhoneCc(e.target.value)}
                          className="w-28 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-2 text-sm text-[hsl(var(--content))]"
                        >
                          {COUNTRY_CODES.map((c) => (
                            <option key={c.code || 'other'} value={c.code}>{c.label}</option>
                          ))}
                        </select>
                        <label htmlFor="ud-phone-rest" className="sr-only">Phone number</label>
                        <Input
                          id="ud-phone-rest"
                          type="tel"
                          inputMode="tel"
                          value={phoneRest}
                          onChange={(e) => setPhoneRest(e.target.value)}
                          placeholder="5551234567"
                          leftElement={<Phone className="h-4 w-4" />}
                        />
                      </div>
                      <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">Stored as E.164 (e.g. +1 5551234567).</p>
                    </div>
                  </div>

                  {/* Photo: upload | stock | custom URL */}
                  <div className="space-y-2">
                    <span className={labeledInput}>Photo</span>
                    <div role="radiogroup" aria-label="Photo source" className="flex flex-wrap gap-2">
                      {([
                        ['upload', 'Upload', Upload],
                        ['stock', 'Stock avatar', Smile],
                        ['custom', 'Custom URL', Link2],
                      ] as const).map(([mode, label, Icon]) => (
                        <button
                          key={mode}
                          type="button"
                          role="radio"
                          aria-checked={photoMode === mode}
                          onClick={() => setPhotoMode(mode)}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                            photoMode === mode
                              ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                              : 'border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {label}
                        </button>
                      ))}
                    </div>

                    {photoMode === 'upload' && (
                      <div className="space-y-2">
                        <label htmlFor="ud-photo-file" className="sr-only">Upload photo file</label>
                        <input
                          id="ud-photo-file"
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          onChange={(e) => onPhotoFile(e.target.files?.[0])}
                          className="block w-full text-sm text-[hsl(var(--content-subtle))] file:mr-3 file:rounded-lg file:border-0 file:bg-brand-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-brand-700"
                        />
                        <p className="text-xs text-[hsl(var(--content-subtle))]">
                          Resized to ~256×256 and stored inline as a data URL (≤256KB) — no blob storage yet.
                        </p>
                        {photoErr && <p role="alert" className="text-xs text-red-600 dark:text-red-400">{photoErr}</p>}
                      </div>
                    )}

                    {photoMode === 'stock' && (
                      <div>
                        <span className="block text-xs text-[hsl(var(--content-subtle))] mb-1">Pick a built-in avatar</span>
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Stock avatars">
                          {STOCK_AVATAR_COLORS.map((_, i) => {
                            const avatar = stockAvatar(form.firstName || data.email, i);
                            return (
                              <button
                                key={i}
                                type="button"
                                role="radio"
                                aria-checked={form.photoUrl === avatar}
                                aria-label={`Stock avatar ${i + 1}`}
                                onClick={() => setForm((f) => ({ ...f, photoUrl: avatar }))}
                                className={`h-11 w-11 rounded-full overflow-hidden border-2 transition-transform hover:scale-105 ${
                                  form.photoUrl === avatar ? 'border-brand-500' : 'border-transparent'
                                }`}
                              >
                                <img src={avatar} alt="" className="h-full w-full" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {photoMode === 'custom' && (
                      <div>
                        <label htmlFor="ud-photo" className="sr-only">Photo URL</label>
                        <Input id="ud-photo" value={form.photoUrl} onChange={(e) => setForm((f) => ({ ...f, photoUrl: e.target.value }))} placeholder="https://…" />
                        <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">
                          Paste a hosted image URL (e.g. SharePoint/Teams profile picture link).
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="ud-notes" className={labeledInput}>Notes</label>
                    <textarea
                      id="ud-notes"
                      value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      rows={3}
                      className={inputCls + ' resize-y'}
                      placeholder="Admin notes about this user"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Button variant="primary" size="sm" disabled={busy} onClick={saveDetails}>
                      {busy ? 'Saving…' : 'Save changes'}
                    </Button>
                    {savedAt && <span className="text-xs text-green-600 dark:text-green-400" role="status">Saved ✓</span>}
                  </div>
                </TabsContent>

                {/* ── Account & Roles tab ──────────────────────────────────── */}
                <TabsContent value="account" className="space-y-4 mt-4">
                  <section aria-labelledby="membership-info" className="space-y-2">
                    <h4 id="membership-info" className="text-sm font-semibold text-[hsl(var(--content))]">Membership &amp; role</h4>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[data.role] ?? ''}`}>
                        {ROLE_LABEL[data.role] ?? data.role}
                      </span>
                      <span className="text-xs text-[hsl(var(--content-subtle))]">
                        Change the role from the users table.
                      </span>
                    </div>
                    <div className="text-sm text-[hsl(var(--content-subtle))]">
                      {data.deactivated ? (
                        <span className="text-red-600 dark:text-red-400 font-medium">Deactivated — sign-in blocked</span>
                      ) : (
                        <span>Account active</span>
                      )}
                    </div>
                    {data.technician ? (
                      <p className="text-xs text-[hsl(var(--content-subtle))]">
                        Linked technician record: <span className="font-medium text-[hsl(var(--content))]">{data.technician.name}</span>
                        {data.technician.cwMemberId != null ? <> · CW member #{data.technician.cwMemberId}</> : null}
                        {data.technician.tier ? <> · {data.technician.tier}</> : null}
                        {data.technician.active ? '' : ' · inactive'}
                      </p>
                    ) : (
                      <p className="text-xs text-[hsl(var(--content-subtle))] italic">No technician record linked.</p>
                    )}
                  </section>
                  <section aria-labelledby="memberships-section" className="space-y-2">
                    <h4 id="memberships-section" className="text-sm font-semibold text-[hsl(var(--content))]">Tenant memberships</h4>
                    <ul className="text-sm text-[hsl(var(--content-subtle))] space-y-1">
                      {data.memberships.map((m, i) => (
                        <li key={m.tenantId}>
                          {ROLE_LABEL[m.role] ?? m.role} · tenant {m.tenantId.slice(0, 8)}…{i === 0 ? ' (current)' : ''}
                        </li>
                      ))}
                    </ul>
                  </section>
                </TabsContent>

                {/* ── Skills tab ───────────────────────────────────────────── */}
                <TabsContent value="skills" className="space-y-4 mt-4">
                  {!data.technician ? (
                    <p className="text-xs text-[hsl(var(--content-subtle))] italic">
                      No technician record is linked to this user, so skill levels can't be set here. Manage skills on the Technicians page.
                    </p>
                  ) : (
                    <>
                      {/* Add a new skill (creates it in the taxonomy if missing) */}
                      <section aria-labelledby="add-skill" className="rounded-xl border border-[hsl(var(--border))] p-3 space-y-2">
                        <h4 id="add-skill" className="text-sm font-semibold text-[hsl(var(--content))]">Add a skill</h4>
                        <div className="flex flex-wrap items-end gap-2">
                          <div className="flex-1 min-w-40">
                            <label htmlFor="ud-new-skill" className="sr-only">New skill name</label>
                            <Input
                              id="ud-new-skill"
                              value={newSkillName}
                              onChange={(e) => setNewSkillName(e.target.value)}
                              placeholder="e.g. HVAC Repair (created if missing)"
                            />
                          </div>
                          <div>
                            <label htmlFor="ud-new-skill-level" className="sr-only">Initial level</label>
                            <select
                              id="ud-new-skill-level"
                              value={newSkillLevel}
                              onChange={(e) => setNewSkillLevel(Number(e.target.value))}
                              className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-2 text-sm text-[hsl(var(--content))]"
                            >
                              {[0, 1, 2, 3, 4].map((l) => (
                                <option key={l} value={l}>{l} — {LEVEL_LABELS[l]}</option>
                              ))}
                            </select>
                          </div>
                          <Button variant="secondary" size="sm" disabled={busy || !newSkillName.trim()} onClick={addSkill}>
                            Add
                          </Button>
                        </div>
                        <p className="text-xs text-[hsl(var(--content-subtle))]">Creates the skill in the taxonomy as needed; recorded as source=admin.</p>
                      </section>

                      {/* Taxonomy with per-skill levels */}
                      {[...skillsByCategory.entries()].map(([category, skills]) => (
                        <div key={category} className="space-y-2">
                          <p className="text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">{category}</p>
                          {skills.map((s) => (
                            <div key={s.skillId} className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`text-xs px-2 py-0.5 rounded font-medium shrink-0 ${LEVEL_COLORS[s.level]}`}>{LEVEL_LABELS[s.level]}</span>
                                <span className="text-sm text-[hsl(var(--content))] truncate">{s.skillName}</span>
                                {s.source === 'inferred' && s.level > 0 && (
                                  <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium shrink-0">AI</span>
                                )}
                              </div>
                              <select
                                value={s.level}
                                onChange={(e) => setSkill(s.skillId, Number(e.target.value))}
                                className="text-xs rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1 text-[hsl(var(--content))] shrink-0"
                                aria-label={`Skill level for ${s.skillName}`}
                              >
                                {[0, 1, 2, 3, 4].map((l) => (
                                  <option key={l} value={l}>{l} — {LEVEL_LABELS[l]}</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                </TabsContent>

                {/* ── 3CX & Microsoft tab ──────────────────────────────────── */}
                <TabsContent value="integrations" className="space-y-5 mt-4">
                  <section aria-labelledby="threecx-section" className="space-y-2">
                    <h4 id="threecx-section" className="text-sm font-semibold text-[hsl(var(--content))]">3CX extension</h4>
                    <div>
                      <label htmlFor="ud-3cx" className={labeledInput}>Extension</label>
                      <ThreeCxExtensionSelector id="ud-3cx" value={form.threecxExtension} items={extOptions} disabled={!data.technician || directory.loading || !!directory.error || directory.data?.status === 'unavailable'} loading={directory.loading} onChange={(extension) => setForm((f) => ({ ...f, threecxExtension: extension }))} />
                      {directory.error && <p role="alert" className="text-xs text-red-600 dark:text-red-400 mt-1">Could not load the live 3CX directory. The current assignment is preserved and changes are disabled.</p>}
                      {directory.data?.status === 'unavailable' && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">3CX is not configured for this tenant.</p>}
                      {directory.data?.status === 'ready' && directory.data.items.length === 0 && <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">The live 3CX directory is empty.</p>}
                      {directory.data?.status === 'ready' && directory.data.items.length > 0 && <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">Live from 3CX. Extensions assigned to another technician are unavailable.</p>}
                    </div>
                  </section>

                  <section aria-labelledby="entra-section" className="space-y-2">
                    <h4 id="entra-section" className="text-sm font-semibold text-[hsl(var(--content))]">Microsoft account</h4>
                    <div className="text-xs text-[hsl(var(--content-subtle))] space-y-0.5">
                      <p>Entra object ID: <span className="font-mono">{data.entra?.entraOid ?? '— not linked yet (set at first Microsoft sign-in)'}</span></p>
                      <p>Platform email: <span className="font-mono">{data.entra?.email ?? data.email}</span></p>
                    </div>
                    <div>
                      <label htmlFor="ud-upn" className={labeledInput}>Entra UPN (editable)</label>
                      <Input
                        id="ud-upn"
                        type="email"
                        value={form.entraUpn}
                        onChange={(e) => setForm((f) => ({ ...f, entraUpn: e.target.value }))}
                        placeholder="user@hts.onmicrosoft.com"
                      />
                      <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">
                        Sign-in identity is owned by Entra (the IdP); this UPN is an editable hint/override for display.
                      </p>
                    </div>
                  </section>

                  <div className="flex items-center gap-3">
                    <Button variant="primary" size="sm" disabled={busy} onClick={saveDetails}>
                      {busy ? 'Saving…' : 'Save changes'}
                    </Button>
                    {savedAt && <span className="text-xs text-green-600 dark:text-green-400" role="status">Saved ✓</span>}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
